import {
  Badge,
  Group,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { OffenePunkteWidget } from "../components/OffenePunkteWidget.js";
import { OnlyMineSwitch } from "../components/OnlyMineSwitch.js";
import { VisitReminderWidget } from "../components/VisitReminderWidget.js";
import { MaintenanceWidget } from "../components/maintenance/MaintenanceWidget.js";
import { hasRole, useCurrentUser } from "../hooks/useAuth.js";
import { useInbox } from "../hooks/useCampaigns.js";
import { useExemptionInbox } from "../hooks/useVisitExemptions.js";
import { useCustomers } from "../hooks/useCustomers.js";
import { useOnlyMine } from "../lib/onlyMine.js";

export function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  // „Meine Kunden" oder „mein Bereich" — wirkt auf die Erinnerungs- und die
  // Wartungskachel und reist beim Klick in die Zielseite mit (lib/onlyMine.ts).
  const { onlyMine } = useOnlyMine();

  // Freigaben liefert das Backend nur für Admin/BL/Stellv. (inboxForUser) —
  // für alle anderen Rollen wäre das Widget ein dauerhaft leeres "0".
  const showInboxWidget = hasRole(user, [
    "admin",
    "bereichsleiter",
    "stellvertreter",
  ]);

  // Orphan-Widget nur für BL/Stellv/Admin/GL — Service Leads sehen es nicht.
  const showOrphanWidget = hasRole(user, [
    "bereichsleiter",
    "stellvertreter",
    "admin",
    "geschaeftsleitung",
  ]);

  // Wartungs-Kachel: dieselben fünf Rollen wie die Seite /wartung selbst
  // (RequireRole in router.ts, Spec §6).
  const showMaintenanceWidget = hasRole(user, [
    "admin",
    "geschaeftsleitung",
    "bereichsleiter",
    "stellvertreter",
    "projektleiter",
  ]);

  return (
    <Stack gap="md">
      <Title order={2}>{t("pages.dashboard.title")}</Title>

      <Group align="stretch" gap="md">
        {showInboxWidget && <InboxWidget />}
        {showOrphanWidget && <OrphanCustomersWidget />}
      </Group>

      {showOrphanWidget && <OffenePunkteWidget />}
      <OnlyMineSwitch />
      <VisitReminderWidget onlyMine={onlyMine} />
      {showMaintenanceWidget && <MaintenanceWidget onlyMine={onlyMine} />}
    </Stack>
  );
}

function OrphanCustomersWidget() {
  const { t } = useTranslation();
  const { data, isLoading } = useCustomers({ withoutLead: true, page: 1 });
  const count = data?.total ?? 0;
  return (
    <Paper
      component={Link}
      to="/customers"
      search={{ withoutLead: true } as never}
      p="md"
      withBorder
      style={{
        textDecoration: "none",
        color: "inherit",
        minWidth: 280,
        cursor: "pointer",
      }}
    >
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Text size="xs" c="dimmed" fw={600} tt="uppercase">
            {t("pages.dashboard.orphanLabel")}
          </Text>
          <Text size="sm">{t("pages.dashboard.orphanDescription")}</Text>
        </Stack>
        <ThemeIcon
          size="xl"
          radius="md"
          variant="light"
          color={count > 0 ? "orange" : "gray"}
        >
          <Text fw={700} size="lg">
            {isLoading ? "…" : count}
          </Text>
        </ThemeIcon>
      </Group>
      {count > 0 && (
        <Badge color="orange" variant="light" size="sm" mt="xs">
          {t("pages.dashboard.orphanAction")}
        </Badge>
      )}
    </Paper>
  );
}

function InboxWidget() {
  const { t } = useTranslation();
  const { data: inboxItems, isLoading: campaignsLoading } = useInbox();
  const { data: exemptions, isLoading: exemptionsLoading } = useExemptionInbox();
  const isLoading = campaignsLoading || exemptionsLoading;
  const count = (inboxItems?.length ?? 0) + (exemptions?.length ?? 0);
  return (
    <Paper
      component={Link}
      to="/inbox"
      p="md"
      withBorder
      style={{
        textDecoration: "none",
        color: "inherit",
        minWidth: 280,
        cursor: "pointer",
      }}
    >
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Text size="xs" c="dimmed" fw={600} tt="uppercase">
            {t("pages.dashboard.inboxLabel")}
          </Text>
          <Text size="sm">{t("pages.dashboard.inboxDescription")}</Text>
        </Stack>
        <ThemeIcon
          size="xl"
          radius="md"
          variant="light"
          color={count > 0 ? "yellow" : "gray"}
        >
          <Text fw={700} size="lg">
            {isLoading ? "…" : count}
          </Text>
        </ThemeIcon>
      </Group>
      {count > 0 && (
        <Badge color="yellow" variant="light" size="sm" mt="xs">
          {t("pages.dashboard.inboxActionRequired")}
        </Badge>
      )}
    </Paper>
  );
}
